import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { IconButton, Menu, MenuItem, TableCell, TableRow } from '@mui/material'
import Content from 'components/content/content'
import CONSTANT from 'modules/constant'
import type { PartialConfigItemProps } from 'modules/partial'
import { useGetConfigItemListMutation } from 'store/configItem'
import { useGetVoucherBirthdayDropdownMutation } from 'store/tokenRequest'
import Icon from 'components/icon'
import GLOBAL from 'modules/global'
import type { ConfigItemProps } from "modules/types"
import Detail from './detail/detail'
import Edit from './edit/edit'

const ConfigItemList = () => {
    //   const navigate = useNavigate()
    const [getConfigItem, configItem] = useGetConfigItemListMutation()
    const [getVoucherBirthdayDropdown, voucherBirthdayDropdown] = useGetVoucherBirthdayDropdownMutation()
    const [anchor, setAnchor] = useState<null | HTMLElement>(null)
    const [menu, setMenu] = useState<null | number>(null)

    const [drawerDetail, setDrawerDetail] = useState<boolean>(false)
    const [dialogUpdate, setDialogUpdate] = useState<boolean>(false)

    const [payload, setPayload] = useState<PartialConfigItemProps>({
        start: 0,
        length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
        field: ['name', 'description', 'category', 'value', 'label'].join(','),
        search: { value: '' },
        description: '',
        name: '',
        category: '',
        value: '',
        label: ''
    })

    const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
    const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

    const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: { value } })

    const onAction = (event: React.MouseEvent<HTMLElement>, index: null | number) => {
        setAnchor(event.currentTarget)
        setMenu(index)
    }

    const onResync = () => {
        const formData = new FormData()

        formData.append('start', payload.start.toString())
        formData.append('length', payload.length.toString())
        formData.append('field', payload.field)

        if (!isEmpty(payload.search.value)) formData.append('search[value]', payload.search.value)
        if (!isEmpty(payload.name)) formData.append('name', payload.name)
        if (!isEmpty(payload.category)) formData.append('category', payload.category)
        if (!isEmpty(payload.value)) formData.append('value', payload.value)
        if (!isEmpty(payload.description)) formData.append('description', payload.value)
        if (!isEmpty(payload.label)) formData.append('description', payload.label)

        formData.forEach((value, key) => {
            console.log('🚀 ~ onResync ~ formData:', `${key}: ${value}`)
        })

        getConfigItem(formData)
    }

    const onRemoveAnchor = async (callback: CallableFunction) => {
        setAnchor(null)
        callback && callback(callback)
    }

    const numbers = GLOBAL.tableNumber(payload.start)
    const totalPage = configItem.data && GLOBAL.tableTotalPage(configItem.data.recordsTotal)
    const tableHeadTitles = ['Config Code', 'Config Name', 'Category', 'Description', 'Value']
    const openAnchor = Boolean(anchor)

    // Helper function to get voucher name for birthday voucher configs
    const getValue = (table: ConfigItemProps) => {
        const isBirthdayVoucherConfig = table.name === 'BIRTHDAY_VOUCHER'

        if (isBirthdayVoucherConfig && table.value) {
            if (voucherBirthdayDropdown.isLoading) {
                return 'Loading voucher...'
            }
            if (voucherBirthdayDropdown.data) {
                const voucher = voucherBirthdayDropdown.data.find(item => item.id === +table.value)
                return voucher ? voucher.name : table.value
            }
            return table.value
        }
        return table.value || '-'
    }

    useEffect(() => {
        onResync()
    }, [payload])

    // Fetch voucher dropdown data if there are any birthday voucher configs
    useEffect(() => {
        const hasBirthdayVoucherConfig = configItem.data?.data?.some(table => table.name === 'BIRTHDAY_VOUCHER')
        if (hasBirthdayVoucherConfig && !voucherBirthdayDropdown.data) {
            getVoucherBirthdayDropdown()
        }
    }, [configItem.data, voucherBirthdayDropdown.data, getVoucherBirthdayDropdown])

    return (
        <>
            <Content
                title='Config List'
                tableHeadTitles={tableHeadTitles}
                onSearch={onSearch}
                pagination={onChangePage}
                onChangePerPage={onChangePerPage}
                totalPage={totalPage}
                totalRecords={configItem.data && configItem.data.recordsTotal}
                page={payload.start + 1}
                isLoading={configItem.isLoading}
            >
                {configItem.isSuccess &&
                    configItem.data &&
                    configItem.data.data.map((table, index) => (
                        <TableRow key={index}>
                            <TableCell>{numbers[index] + 1}</TableCell>
                            <TableCell>
                                <IconButton
                                    id={`button-${index}-${table.id}`}
                                    aria-haspopup='true'
                                    aria-controls={openAnchor ? `menu-${index}-${table.id}` : undefined}
                                    aria-expanded={openAnchor ? 'true' : undefined}
                                    onClick={(event) => onAction(event, index)}
                                >
                                    <Icon icon='MoreHoriz' />
                                </IconButton>
                                <Menu
                                    id={`menu-${index}-${table.id}`}
                                    aria-labelledby={`button-${index}-${table.id}`}
                                    anchorEl={anchor}
                                    open={openAnchor && menu == index}
                                    onClose={() => onRemoveAnchor(() => setMenu(null))}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }}
                                >
                                    <MenuItem onClick={() => onRemoveAnchor(() => setDrawerDetail(true))}>
                                        View
                                    </MenuItem>
                                    <MenuItem onClick={() => onRemoveAnchor(() => setDialogUpdate(true))}>
                                        Edit
                                    </MenuItem>
                                </Menu>
                                <Detail
                                    id={table.id}
                                    open={drawerDetail && menu == index}
                                    onClose={() => setDrawerDetail(false)}
                                />
                                {dialogUpdate && menu == index && (
                                    <Edit
                                        id={table.id}
                                        open={dialogUpdate && menu == index}
                                        onClose={() => setDialogUpdate(false)}
                                    />
                                )}
                            </TableCell>
                            <TableCell>{table.name || '-'}</TableCell>
                            <TableCell>{table.label || '-'}</TableCell>
                            <TableCell>{table.configGroupName || '-'}</TableCell>
                            <TableCell>{table.description || '-'}</TableCell>
                            <TableCell>{getValue(table)}</TableCell>
                        </TableRow>
                    ))}
            </Content>
        </>
    )
}

export default ConfigItemList
